import { useMemo } from 'react';
import { useFeatureFlags } from '../routing/useFeatureFlags';
import { getTrustSignals, TRUST_SIGNAL_FEATURES, type TrustSignal } from '../../components/location/features/featureSubDetails';
import type { LocationFeatureDetailsByName } from '../../types/Location';

interface LocationTrustSignalsResult {
  trustSignals: TrustSignal[];
  /** Features left for the card's icon list: everything except trust-signal features. */
  otherFeatures: string[] | null | undefined;
}

/**
 * Splits a search card's features into trust-signal chips and the remaining icon list.
 * With `locationFeatureDetails` off, returns no signals and the original `features` array unchanged.
 */
export function useLocationTrustSignals(
  features: string[] | null | undefined,
  featureDetails: LocationFeatureDetailsByName | null | undefined
): LocationTrustSignalsResult {
  const { locationFeatureDetails } = useFeatureFlags();

  return useMemo(() => {
    if (!locationFeatureDetails) return { trustSignals: [], otherFeatures: features };
    // Excludes all trust-signal features, not just those with a chip: "Bonded" marked No gets no chip
    // and must not reappear in the icon list as an amenity.
    const trustFeatures = new Set<string>(TRUST_SIGNAL_FEATURES);
    return {
      trustSignals: getTrustSignals(features, featureDetails),
      otherFeatures: features?.filter((feature) => !trustFeatures.has(feature)),
    };
  }, [locationFeatureDetails, features, featureDetails]);
}
